import { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { logout, toastMessagesAdd, updateOrderFromEvent, refreshAccountBalance } from 'src/actions';
import { mapOrderUpdateEventDtoToModel } from 'src/mappers';
import { v4 as uuid } from 'uuid';

import { EnvConstant } from '../configs/env.configs';
import { FromTradingBlockServerEvent, FromTradingBlockServerEventDto } from '../events/FromTradingBlockServerEvent';
import { Logger } from '../utils/Logger';
import { parseTradingBlockMessageEvent } from '../utils/parseTradingBlockMessageEvent';

import { useAccountSelector } from './useAccountSelector';

export const useTradingBlockWebSocket = () => {
  const dispatch = useDispatch();

  const tbToken: string = useSelector((state: any) => state.auth.data.tbToken);
  const { account } = useAccountSelector();

  // TODO: move to sagas
  const refreshDashboardIfNecessary = (event: FromTradingBlockServerEvent.OrderUpdateEvent) => {
    if (!account?.accountId) {
      return;
    }

    if (event.payload.status.isCompleted || event.payload.fillQuantity > 0) {
      dispatch(refreshAccountBalance(account.accountId));

      return;
    }

    // NOTE: when buy order is cancelled, reserved cash is released
    if (event.payload.status.isCancelled && event.payload.action.isBuy) {
      dispatch(refreshAccountBalance(account.accountId));

      return;
    }

    // NOTE: when buy order is pending new, reserved cash is put on hold
    if (event.payload.status.isPendingNew && event.payload.action.isBuy) {
      dispatch(refreshAccountBalance(account.accountId));

      return;
    }
  };

  const handleOrderUpdateEvent = (event: FromTradingBlockServerEvent.OrderUpdateEvent) => {
    // NOTE: for some reason TradingBlock sends the first order update event with id = 0, status = Acknowledged
    if (event.payload.id && event.payload.id !== '0') {
      dispatch(updateOrderFromEvent(event.payload));
      refreshDashboardIfNecessary(event);
    }
  };

  const handleSessionExpireEvent = () => {
    dispatch(logout());
    dispatch(
      toastMessagesAdd({
        key: uuid(),
        message: 'Your session has expired. Please login again.',
        severity: 'error',
      }),
    );
  };

  useEffect(() => {
    if (account?.accountId && EnvConstant.TRADING_BLOCK_WEB_SOCKET_URL) {
      // Create a WebSocket connection
      const socket = new WebSocket(EnvConstant.TRADING_BLOCK_WEB_SOCKET_URL);

      socket.addEventListener('open', () => {
        Logger.info('TradingBlock WebSocket connection is open. Sending initial subscribe message...');
        socket.send(`0=${tbToken}|1=${account?.accountId}|9=1|13=1`);
        Logger.info('TradingBlock WebSocket initial subscribe message was sent.');
      });

      socket.addEventListener('close', () => {
        Logger.info('TradingBlock WebSocket connection is closed.');
      });

      socket.addEventListener('message', (event: MessageEvent<string>) => {
        try {
          const eventDto = parseTradingBlockMessageEvent(event);

          if (eventDto[1] === FromTradingBlockServerEventDto.EventDtoType.ORDER_UPDATE) {
            Logger.debug('Received TradingBlock WebSocket order update event:', JSON.stringify(event.data));
            handleOrderUpdateEvent(mapOrderUpdateEventDtoToModel(eventDto));
          }

          if (eventDto[1] === FromTradingBlockServerEventDto.EventDtoType.SESSION_EXPIRE) {
            Logger.debug('Received TradingBlock WebSocket session expire event:', JSON.stringify(event.data));
            handleSessionExpireEvent();
          }
        } catch (error) {
          Logger.error('Error handling TradingBlock event. Received message: ', event.data);
          Logger.error(error);
        }
      });

      socket.addEventListener('error', event => {
        Logger.error('TradingBlock WebSocket error:', event);
      });

      return () => {
        socket.close();
      };
    }
  }, [tbToken, account?.accountId]);
};
